import { User } from '../../lib/user'
import { solvesMap } from '../../lib/stats'
import { Game, GameStatus, Actions } from './Game'
import { getToday, WordList, MINI_DAILY_LIST, DAILY_LIST } from '../../lib/words'
import { GameMode, GAME_MODE_DAILY } from '../../constants/strings'
import { useMemo, forwardRef, useState, useEffect, useRef } from 'react'
import { GameStats } from '../../lib/localStorage'
import { Settings } from '../../lib/settings'
import { useNavigate } from 'react-router-dom'
import { ExclamationIcon } from '@heroicons/react/outline'
import { Popover, Transition } from '@headlessui/react'

type Props = {
  mode: GameMode,
  list: WordList,
  user: User
  index: number|null
  settings: Settings
  actions: Actions
  setIsStatsModalOpen: (isOpen: boolean) => void
  onGameStatusChange: (status: GameStatus) => void
  onUpdateStats: (stats: GameStats) => void
  onOpenArchive: () => void
  onHint: () => void
  setResponsive: (isResponsive: boolean) => void
}

export const DailyGame = forwardRef(({
  mode,
  list,
  user,
  settings,
  actions,
  setIsStatsModalOpen,
  index,
  onGameStatusChange,
  onUpdateStats,
  onOpenArchive,
  onHint,
  setResponsive,
}: Props, ref) => {
  const [isPopoverShowing, setIsPopoverShowing] = useState(false)

  const [hasSolvedBefore, setHasSolvedBefore] = useState(false)
  const navigate = useNavigate()
  const isArchive = index !== null;
  const todayIndex = list.getIndex(getToday())
  const solution = useMemo(() => {
    if(index && index > todayIndex) {
      throw new Error("index exceeds daily")
    }
    return isArchive ? list.getSolution(index) : list.getDailySolution(getToday())
  }, [index, isArchive, todayIndex, list])

  const prevSolution = useRef<string>("")

  useEffect(() => {
    const hasSolved = solvesMap(user.stats()).has(`${mode},${solution.word}`)
    if(solution.word !== prevSolution.current) {
      setHasSolvedBefore(hasSolved)
    }
    prevSolution.current = solution.word
  }, [solution, user, mode])

  const modeSlug = useMemo(() => {
    return mode === GAME_MODE_DAILY ? 'daily' : 'mini'
  }, [mode])

  let header = <></>;


  const next = () => {
    if(index === null) {
      return
    }
    navigate(`/${modeSlug}/${index + 1}`)
  }

  const prev = () => {
    if(index === null) {
      return
    }
    navigate(`/${modeSlug}/${index - 1}`)
  }

  const noStatsPopover = <Popover className="right-0 flex items-center justify-center absolute">
    <Popover.Button
      onMouseEnter={() => setIsPopoverShowing(true)}
      onMouseLeave={() => setIsPopoverShowing(false)}
      onFocus={() => setIsPopoverShowing(true)}
      onBlur={() => setIsPopoverShowing(false)} >
      <ExclamationIcon 
      className="h-5 w-5 scale-up cursor-pointer dark:stroke-white" />
    </Popover.Button>
    <Transition
      enter="transition ease-out duration-200"
      enterFrom="opacity-0 translate-y-1"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-1"
      show={isPopoverShowing}
    >
    <Popover.Panel className="absolute z-10" >
      <div className="w-32 overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
        <p className="text-sm bg-white p-4">
        You have previously solved this puzzle. No stats will be tracked on this solve.
        </p>
      </div>
    </Popover.Panel>
    </Transition>
  </Popover>

  const buttonClass = "title flex justify-center px-2 md:py-2 text-sm uppercase text-gray-500 hover:font-bold dark:text-gray-300 hover:text-indigo-700"
  if(list === MINI_DAILY_LIST || isArchive) {
      header = 
      <div className="flex items-center justify-center">
        <h2 className="flex relative md:py-4 items-center justify-center md:h-6 w-24 text-sm text-gray-500 dark:text-gray-300">
          <span className="title uppercase ">
            {list === MINI_DAILY_LIST && 'Mini '}
            {isArchive && list === DAILY_LIST && 'Daily'}
          </span>
          {isArchive && hasSolvedBefore && noStatsPopover}
        </h2>
      </div>
  }
  if(isArchive) {
    header = <>
      {header}
      <div className="flex items-center justify-center">
        <div className="flex w-22 justify-end">
          {index > 0 && <div className={buttonClass} onClick={prev}> {'《'} Prev </div>}
        </div>
        <h2 className={buttonClass} onClick={onOpenArchive}>
          Archive
        </h2>
        <div className="flex w-24 justify-start">
          {index < todayIndex - 1 && <div className={buttonClass} onClick={next}>Next {'》'} </div>}
        </div>
      </div>
    </>
  }
  return (<>
    <Game
      key={mode + index}
      ref={ref}
      isArchive={isArchive}
      captureStats={!hasSolvedBefore}
      list={list}
      user={user}
      solution={solution}
      mode={mode}
      settings={settings}
      actions={actions}
      setIsStatsModalOpen={setIsStatsModalOpen}
      onGameStatusChange={onGameStatusChange}
      onUpdateStats={onUpdateStats}
      onHint={onHint}
      setResponsive={setResponsive}
      header={header}
    />
  </>
  )
})

